/**
 * E002-T018: WorkflowSelector Component
 *
 * The automation's workflow: a field that opens the workflows as a list, by
 * category, with search, "none" and the option to create one.
 */

import { notify } from '@affine/component';
import { useConnectivityStatus } from '@affine/core/components/hooks/use-connectivity-status';
import { useLocalePicker } from '@affine/core/hooks/use-locale-picker';
import { useI18n } from '@affine/i18n';
import { PlayIcon } from '@blocksuite/icons/rc';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import clsx from 'clsx';
import type { FC } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { EditorService } from '../../../editor';
import { WorkspaceService } from '../../../workspace';
import { type WorkflowInfo, WorkflowService } from '../../services/workflow';
import { WorkflowClientService } from '../../services/workflow-client';
import { DropdownChevron } from '../../shared/dropdown-chevron';
import { WorkflowPicker } from '../../workflow-viewer/workflow-picker';
import * as panelStyles from '../automation-panel.css';
import * as styles from './workflow-selector.css';

// =============================================================================
// sl-gwg9.4: Missing Verified Templates Hook
// =============================================================================

interface VerifiedTemplate {
  id: string;
  /** sl-k94y.1.1: Multi-language title map keyed by BCP-47 locale. */
  titlesI18n: Record<string, string>;
}

function useMissingVerifiedTemplates(
  workspaceId: string,
  existingWorkflows: WorkflowInfo[]
): { missing: VerifiedTemplate[]; importing: boolean; importAll: () => void } {
  const t = useI18n();
  const [verified, setVerified] = useState<VerifiedTemplate[]>([]);
  const [importing, setImporting] = useState(false);
  const workflowClient = useService(WorkflowClientService);
  const picker = useLocalePicker();

  useEffect(() => {
    fetch(
      `/api/marketplace/templates?verified=true&publisher=streamlain&page=1&limit=50`,
      {
        credentials: 'include',
      }
    )
      .then(r => r.json())
      .then((data: { items?: VerifiedTemplate[] }) =>
        setVerified(data.items ?? [])
      )
      .catch(() => {});
  }, [workspaceId]);

  const missing = useMemo(() => {
    const existingNames = new Set(existingWorkflows.map(w => w.name));
    return picker
      ? verified.filter(v => !existingNames.has(picker.pickTitle(v.titlesI18n)))
      : [];
  }, [existingWorkflows, picker, verified]);

  const importAll = useCallback(() => {
    if (importing || missing.length === 0 || !picker) return;
    setImporting(true);
    void (async () => {
      for (const template of missing) {
        try {
          await workflowClient.createWorkflow(
            picker.pickTitle(template.titlesI18n)
          );
        } catch (error) {
          notify.error({
            title: t['com.streamlain.streamlain.workflow.request-failed'](),
            message: error instanceof Error ? error.message : String(error),
          });
        }
      }
      setImporting(false);
    })();
  }, [importing, missing, workflowClient, picker, t]);

  return { missing, importing, importAll };
}

interface WorkflowSelectorProps {
  /** Currently selected workflow ID */
  value: string | null;
  /** Callback when the user picks an existing workflow from the list. */
  onChange: (workflow: WorkflowInfo) => void;
  /**
   * Callback when the user creates a brand-new workflow via the
   * "New workflow" action inside the dropdown. The caller MUST reset all
   * input/output field mappings because a freshly-created workflow has a
   * blank template and any mappings from the previously-selected workflow
   * are stale by definition — keeping them would leave the sidebar in a
   * half-configured state pointing at fields that never existed in the new
   * workflow. Falls back to `onChange` when not provided.
   */
  onCreate?: (workflow: WorkflowInfo) => void;
  /** Callback to clear the selection (deselect workflow) */
  onClear?: () => void;
  /** Shown in the field while no workflow is selected. */
  placeholder: string;
}

export const WorkflowSelector: FC<WorkflowSelectorProps> = ({
  value,
  onChange,
  onCreate,
  onClear,
  placeholder,
}) => {
  const t = useI18n();
  const workspaceId = useService(WorkspaceService).workspace.id;
  const workflowService = useService(WorkflowService);
  const workflowClient = useService(WorkflowClientService);
  const editor = useServiceOptional(EditorService)?.editor;
  const workflows = useLiveData(workflowService.workflows$);
  const error = useLiveData(workflowService.error$);
  const workflowOnline = useLiveData(workflowService.connectivity$);
  const connectivityStatus = useConnectivityStatus();
  const isWorkflowUnavailable =
    connectivityStatus.offline || workflowOnline === false;
  const offlineSelectorTitle =
    t['com.streamlain.streamlain.workflow.select.offline-title']();
  const offlineSelectorMessage =
    t['com.streamlain.streamlain.workflow.select.offline-message']();
  const showOfflineSelectorToast = useCallback(() => {
    notify.warning({
      title: offlineSelectorTitle,
      message: offlineSelectorMessage,
    });
  }, [offlineSelectorMessage, offlineSelectorTitle]);

  // sl-gwg9.4: Check for missing verified templates
  const {
    missing: missingTemplates,
    importing: importingTemplates,
    importAll: importMissingTemplates,
  } = useMissingVerifiedTemplates(workspaceId, workflows);

  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  // Compute position synchronously on open
  useLayoutEffect(() => {
    if (open && buttonRef.current) {
      setRect(buttonRef.current.getBoundingClientRect());
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const selectedWorkflow = value
    ? workflows.find((wf: WorkflowInfo) => wf.id === value)
    : null;

  const handleSelect = useCallback(
    (workflow: WorkflowInfo) => {
      onChange(workflow);
      setOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onClear?.();
    setOpen(false);
  }, [onClear]);

  const handleCreate = useCallback(
    async (name: string): Promise<WorkflowInfo> => {
      const raw = await workflowClient.createWorkflow(name, 'blank');
      await workflowService.refreshWorkflows();
      const info: WorkflowInfo = {
        id: raw.id,
        name: raw.name,
        active: raw.active,
        hasWebhook: raw.hasWebhook,
        webhookUrl: raw.webhookUrl,
        categories: raw.categories ?? [],
      };
      // sl-ai-dropdown-race follow-up: Route fresh workflows through
      // `onCreate` so the caller can reset stale input/output mappings.
      // `onChange` runs the smart-diff pipeline which keeps mappings whose
      // field ids accidentally match between the old and the new schema —
      // for a freshly-created (blank) workflow that's never what the user
      // wants.
      (onCreate ?? onChange)(info);
      setOpen(false);
      // Open the workflow fullscreen viewer so user can work on it
      if (editor) {
        editor.openWorkflowFullscreen(info.id, info.name);
      } else {
        window.dispatchEvent(
          new CustomEvent('streamlain:workflow-fullscreen', {
            detail: { workflowId: info.id, workflowName: info.name },
          })
        );
      }
      return info;
    },
    [workflowClient, workflowService, onChange, onCreate, editor]
  );

  if (error) {
    return (
      <div className={panelStyles.fieldRowStatic} role="alert">
        <span className={styles.loadError}>
          {t['com.streamlain.streamlain.workflow.picker.load-failed']({
            error,
          })}
        </span>
      </div>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={clsx(panelStyles.fieldRow, styles.trigger)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-disabled={isWorkflowUnavailable}
        title={isWorkflowUnavailable ? offlineSelectorMessage : undefined}
        data-testid="workflow-selector"
        onClick={() => {
          if (isWorkflowUnavailable) {
            showOfflineSelectorToast();
            return;
          }
          setOpen(prev => !prev);
        }}
      >
        <PlayIcon className={styles.triggerIcon} />
        <span
          className={clsx(
            panelStyles.fieldLabel,
            !selectedWorkflow && styles.placeholder
          )}
        >
          {selectedWorkflow ? selectedWorkflow.name : placeholder}
        </span>
        <DropdownChevron />
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={popoverRef}
            className={styles.popover}
            data-testid="workflow-selector-popover"
            style={{
              top: rect.bottom + 4,
              left: rect.left,
              width: rect.width,
              maxHeight: `calc(100vh - ${rect.bottom + 16}px)`,
            }}
          >
            <WorkflowPicker
              workflows={workflows}
              selectedId={value}
              onSelect={handleSelect}
              onCreate={handleCreate}
              onClear={onClear ? handleClear : undefined}
              createUnavailable={isWorkflowUnavailable}
              filter="all"
              autoFocusSearch
              footer={
                // sl-gwg9.4: verified templates the workspace does not have yet
                missingTemplates.length > 0 && (
                  <div className={styles.templates}>
                    <span>
                      {t['com.streamlain.streamlain.workflow.picker.templates'](
                        { count: missingTemplates.length }
                      )}
                    </span>
                    <button
                      type="button"
                      className={styles.templatesAction}
                      onClick={importMissingTemplates}
                      disabled={importingTemplates}
                    >
                      {importingTemplates
                        ? t[
                            'com.streamlain.streamlain.workflow.picker.importing'
                          ]()
                        : t[
                            'com.streamlain.streamlain.workflow.picker.import-all'
                          ]()}
                    </button>
                  </div>
                )
              }
            />
          </div>,
          document.body
        )}
    </>
  );
};
